import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './index.module.css'
import RoundedButton from '../roundedButton'
import RoundedInput from '../roundedInput'
import RoundedSelect from '../roundedSelect'
import { brazilianStates } from '../../utils/brazilianStates'
import { useForm, useWatch } from 'react-hook-form'
import ApiService from '../../ApiService'
import { PropertyRequest, PropertyResponse, UserResponse } from '../../models'
import LoadingButton from '../loadingButton'
import FeedbackComponent from '../feedbackComponent'
import RoundedInputSelect from '../roundedInputSelect'

interface Props {
    closeCallback: () => void
    propertyToEdit: PropertyResponse | undefined
    onSuccessCallback?: () => void | Promise<void>
}

interface FormValues {
    propertyId: number;
    landlord: string;
    tenant: string;
    address: string;
    addressNo: string;
    addressComplement: string;
    addressNeighborhood: string;
    city: string;
    state: string;
    cep: string;
    country: string;
    details: string;
    images: string | FileList | null;
    videos: string | FileList | null;
}

export default function NewPropertyModal(props: Props) {
    const { control, handleSubmit, setValue, watch } = useForm<FormValues>()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [feedback, setFeedback] = useState<{type: 'success' | 'error', message: string} | undefined>()
    const [tenantList, setTenantList] = useState<UserResponse[]>([])
    const [landlordList, setLandlordList] = useState<UserResponse[]>([])
    const landlord = useWatch({ control, name: 'landlord' })
    const tenant = useWatch({ control, name: 'tenant' })
    const cep = useWatch({ control, name: 'cep' })
    const landlordSearchRequestId = useRef(0)
    const tenantSearchRequestId = useRef(0)

    const USER_SEARCH_DEBOUNCE_MS = 400

    const getUserFullName = (user?: UserResponse) => `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()

    const getUserIdByName = (
        list: UserResponse[],
        currentValue: string,
        fallbackId?: number,
        fallbackName?: string
    ) => {
        const normalizedCurrentValue = currentValue.trim()
        const matchedUser = list.find((user) => getUserFullName(user) === normalizedCurrentValue)

        if (matchedUser) {
            return matchedUser.id
        }

        if (fallbackName && normalizedCurrentValue === fallbackName.trim()) {
            return fallbackId ?? 0
        }

        return 0
    }

    const getCep = useCallback(async (cep: string) => {
        const address = await (await fetch(`https://viacep.com.br/ws/${cep}/json/`)).json()
        setValue('address',address?.logradouro)
        setValue('addressNeighborhood', address?.bairro)
        setValue('city',address?.localidade)
        setValue('state', address?.uf)
    }, [setValue])

    const getUser = async (
        value: string,
        accountType: 'PROPRIETARIO' | 'INQUILINO',
        requestId: number
    ) => {
        try {
            const response = await ApiService.getUserBySearchTerm(value)
            if (!response?.data) {
                return
            }

            const filteredUsers = response.data.filter(user => user?.account_type === accountType)
            if (accountType === 'PROPRIETARIO') {
                if (landlordSearchRequestId.current !== requestId) {
                    return
                }
                setLandlordList(filteredUsers)
                return
            }

            if (tenantSearchRequestId.current !== requestId) {
                return
            }
            setTenantList(filteredUsers)
        } catch (error) {
            console.log(error)
        }
    }

    const handleSendData = async (data: FormValues) => {
        setIsLoading(true)
        try {
            const request:PropertyRequest = {
                code: data.propertyId,
                landlord: getUserIdByName(
                    landlordList,
                    data.landlord,
                    props.propertyToEdit?.landlord,
                    props.propertyToEdit?.landlord_name
                ),
                tenant: getUserIdByName(
                    tenantList,
                    data.tenant,
                    props.propertyToEdit?.tenant,
                    props.propertyToEdit?.tenant_name
                ),
                cep: data.cep,
                address: data.address,
                address_no: data.addressNo,
                address_l2: data.addressComplement,
                address_neighborhood: data.addressNeighborhood,
                city: data.city,
                state: data.state,
                country: data.country,
                details: data.details,
                image_folder: null,
                video_folder: null
            }
            if (props.propertyToEdit){
                request.id = props.propertyToEdit.id
                await ApiService.updateProperty(request)
                setFeedback({type:'success', message:'Imóvel atualizado com sucesso.'})
            }
            else {                                                                                          
                await ApiService.createProperty(request)
                setFeedback({type:'success', message:'Imóvel criado com sucesso.'})
            }
            await props.onSuccessCallback?.()
            props.closeCallback()
        } catch (error: any) {
            console.log(error)
            setFeedback({type:'error', message:'Houve um erro ao salvar o imóvel.'})
        }
        finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        setValue('country', 'Brasil')
        setValue('state',"SP")
    },[setValue])

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setFeedback(undefined)
        },5000)

        return () => window.clearTimeout(timeoutId)
    },[feedback])

    useEffect(() => {
        if (props.propertyToEdit) {
            setValue('propertyId',props.propertyToEdit.code)
            setValue('landlord', props.propertyToEdit.landlord_name)
            setValue('tenant', props.propertyToEdit.tenant_name)
            setValue('cep',props.propertyToEdit.cep)
            setValue('address',props.propertyToEdit.address)
            setValue('addressNo', props.propertyToEdit.address_no)
            setValue('addressComplement', props.propertyToEdit.address_l2)
            setValue('addressNeighborhood', props.propertyToEdit.address_neighborhood)
            setValue('city', props.propertyToEdit.city)
            setValue('state', props.propertyToEdit.state)
            setValue('country', props.propertyToEdit.country)
            setValue('details', props.propertyToEdit.details)
        }
    },[props.propertyToEdit, setValue])

    useEffect(() => {
        if(cep && cep.length === 8) {
            getCep(cep)
        }
    },[cep, getCep])


    useEffect(() => {
        const landlordValue = landlord?.trim()
        if (!landlordValue) {
            landlordSearchRequestId.current += 1
            setLandlordList([])
            return
        }

        const requestId = landlordSearchRequestId.current + 1
        landlordSearchRequestId.current = requestId
        const timeoutId = window.setTimeout(() => {
            void getUser(landlordValue, 'PROPRIETARIO', requestId)
        }, USER_SEARCH_DEBOUNCE_MS)

        return () => window.clearTimeout(timeoutId)
    },[landlord])

    useEffect(() => {
        const tenantValue = tenant?.trim()
        if (!tenantValue) {
            tenantSearchRequestId.current += 1
            setTenantList([])
            return
        }

        const requestId = tenantSearchRequestId.current + 1
        tenantSearchRequestId.current = requestId
        const timeoutId = window.setTimeout(() => {
            void getUser(tenantValue, 'INQUILINO', requestId)
        }, USER_SEARCH_DEBOUNCE_MS)

        return () => window.clearTimeout(timeoutId)
    },[tenant])

  return (
    <div className={styles.background}>
        <div className={styles.backButton}>
            <RoundedButton buttonText='Voltar' buttonType='button' onClickCallback={props.closeCallback}/>
        </div>
        <p className={styles.title}>{props.propertyToEdit ? 'Editar Imóvel' : 'Novo Imóvel'}</p>
        <form className={styles.forms} onSubmit={handleSubmit(handleSendData)}>
            <div className={styles.inputRow}>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Código do imóvel' value={watch('propertyId')?.toString()} disabled={props.propertyToEdit? true: false} type='number' onChangeCallback={(value:string) => setValue('propertyId',Number(value))}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInputSelect label='Proprietário' value={watch('landlord')} items={landlordList?.map((landlord) => `${landlord?.first_name} ${landlord?.last_name}`) ?? ['']} onChange={(value:string) => setValue('landlord',value)}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInputSelect label='Inquilino' value={watch('tenant')} items={tenantList?.map((tenant) => `${tenant?.first_name} ${tenant?.last_name}`) ?? ['']} onChange={(value:string) => setValue('tenant',value)}/>
                </div>
            </div>
            <div className={styles.inputRow}>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Endereço' value={watch('address')} type='text' onChangeCallback={(value:string) => setValue('address',value)}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Número' value={watch('addressNo')} type='number' onChangeCallback={(value:string) => setValue('addressNo',value)}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Complemento' value={watch('addressComplement')} type='text' onChangeCallback={(value:string) => setValue('addressComplement',value)}/>
                </div>
            </div>
            <div className={styles.inputRow}>
                <div className={styles.mediumInput}>
                    <RoundedInput label='CEP' type='text' value={watch('cep')} onChangeCallback={(value:string) => setValue('cep',value)}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Bairro' type='text' value={watch('addressNeighborhood')} onChangeCallback={(value:string) => setValue('addressNeighborhood',value)}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Cidade' type='text' value={watch('city')} onChangeCallback={(value:string) => setValue('city',value)}/>
                </div>
            </div>
            <div className={styles.lastInputRow}>
                <div className={styles.mediumInput}>
                    <RoundedSelect label='Estado' value={watch('state')} items={brazilianStates} onChange={(value:string) => setValue('state',value)}/>
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='País' type='text' value={watch('country')} onChangeCallback={(value:string) => setValue('country',value)}/>
                </div>
            </div>
            <div className={styles.inputRow}>
                <div className={styles.tallInput}>
                    <RoundedInput label='Observações' value={watch('details')} multiline={true} type='text' onChangeCallback={(value:string) => setValue('details',value)} />
                </div>
            </div>
            <div className={styles.inputRow}>
                {feedback? 
                    <div className={styles.feedback}>
                        <FeedbackComponent type={feedback.type} message={feedback.message} />
                    </div>
                :
                    <div className={styles.sendButton}>
                        {isLoading?
                            <LoadingButton />
                        :
                            <RoundedButton buttonText='Salvar' buttonType='submit' />
                        }
                    </div>
                }   
            </div>
        </form>
    </div>
  )
}
